/**
 * @fileoverview GET handler for the "/data_objects/:type_name/:external_id/object_relationships"
 * endpoint. The backend returns a unified paginated list (`{ hits, results }`) with each row tagged
 * by `direction` so the table can render incoming (parent) and outgoing (subaccount) edges together.
 */
import fetch from "@lib/fetch";

import { reactQueryTableFetcherAdapter } from "../reactQueryFetcherAdapters";
import { customObjectsTypeNameExternalIdObjectRelationships } from "./constants";
import type {
  CustomObjectsTypeNameExternalIdObjectRelationshipsParams,
  CustomObjectsTypeNameExternalIdObjectRelationshipsResult,
  CustomObjectsTypeNameExternalIdObjectRelationshipsResultItem,
  RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse,
  RawObjectRelationshipRow,
} from "./types";
import {
  isCustomObjectsTypeNameExternalIdObjectRelationshipsResult,
  isRawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse,
} from "./validators";

export const toObjectRelationshipTableRow = (
  row: RawObjectRelationshipRow
): CustomObjectsTypeNameExternalIdObjectRelationshipsResultItem => ({
  // Row id mirrors the edge's full natural key (direction + related type + rel_kind + external id)
  // so it stays unique: external_id is scoped per type, so two different-type objects can share one.
  id: `${row.direction}:${row.type_name ?? ""}:${row.rel_kind ?? ""}:${row.external_id}`,
  external_id: row.external_id,
  display_name: row.display_name ?? null,
  type_name: row.type_name ?? null,
  rel_kind: row.rel_kind ?? null,
  relationship_name: row.relationship_name ?? null,
  direction: row.direction,
});

export const mapObjectRelationshipsResponse = (
  response: RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse
): CustomObjectsTypeNameExternalIdObjectRelationshipsResult => {
  return {
    hits: response.hits,
    results: response.results.map(toObjectRelationshipTableRow),
  };
};

export const getDataTableCustomObjectsTypeNameExternalIdObjectRelationships = reactQueryTableFetcherAdapter(
  customObjectsTypeNameExternalIdObjectRelationships,
  (
    signal: AbortSignal,
    {
      appGroupId,
      typeName,
      externalId,
      limit = 12,
      start = 0,
      rel_kind,
    }: CustomObjectsTypeNameExternalIdObjectRelationshipsParams
  ): Promise<CustomObjectsTypeNameExternalIdObjectRelationshipsResult> => {
    return fetch<RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse>(
      `/data_objects/${encodeURIComponent(typeName)}/${encodeURIComponent(externalId)}/object_relationships`,
      "GET",
      {
        app_group_id: appGroupId,
        limit,
        start,
        rel_kind,
      },
      { signal },
      { propagateFetchRequestError: false }
    ).then((response) => {
      if (!isRawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse(response)) {
        throw new Error("Invalid object relationships response");
      }

      return mapObjectRelationshipsResponse(response);
    });
  },
  isCustomObjectsTypeNameExternalIdObjectRelationshipsResult
);
